<template>
	<div class="client-documents-list">
		<header class="list-header">
			<div class="list-title-group">
				<h2 class="list-title">Client documents</h2>
			</div>
		</header>

		<div class="controls-row">
			<div class="action-bar">
				<div class="create-wrapper">
					<button class="action-btn action-btn--primary" type="button" :disabled="creating" @click.stop="toggleCreateMenu">
						<AppointmentIcon name="plus" class="action-btn-icon" />
						<span>{{ creating ? 'Creating...' : 'New document' }}</span>
					</button>
					<div v-if="showCreateMenu" class="create-menu" role="menu">
						<button type="button" class="create-menu-option" role="menuitem" @click.stop="handleCreateQuotation">
							Quotation
						</button>
						<button type="button" class="create-menu-option" role="menuitem" @click.stop="handleCreateProposal">
							Proposal
						</button>
						<button type="button" class="create-menu-option" role="menuitem" @click.stop="chooseProposalFile">
							Proposal from Word…
						</button>
					</div>
					<input
						ref="proposalFileInput"
						type="file"
						class="proposal-file-input"
						accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
						tabindex="-1"
						aria-hidden="true"
						@change="onProposalFileChosen"
					/>
				</div>
			</div>
			<section class="filters-bar" aria-label="Document filters">
				<div class="filters-bar-start">
					<AppointmentIcon name="filter" class="filter-bar-icon" />
				</div>
				<div class="search-wrapper">
					<AppointmentIcon name="search" :size="16" class="search-icon" />
					<input
						v-model="search"
						type="text"
						class="input search-input"
						data-refresh-safe
						placeholder="Search..."
						aria-label="Search client documents"
					/>
				</div>
				<div class="category-filter-group" role="group" aria-label="Filter by category">
					<button
						v-for="category in categoryFilters"
						:key="category.value"
						type="button"
						class="category-filter-btn"
						:class="{ active: activeCategory === category.value }"
						:aria-pressed="activeCategory === category.value"
						@click="selectCategory(category.value)"
					>
						{{ category.label }}
					</button>
				</div>
			</section>
		</div>

		<div class="content-wrapper">
			<div class="table-wrapper">
				<div v-if="showInitialLoading" class="loading-state">Loading client documents...</div>
				<div v-else-if="error" class="error-state">
					{{ error }}
					<button class="link-btn" type="button" @click="refresh">Retry</button>
				</div>
				<div v-else class="documents-table-container">
					<table
						class="documents-table"
						:class="{ 'documents-table--with-invoice': showInvoiceColumn }"
					>
						<thead>
							<tr>
								<th class="col-type">Type</th>
								<th class="col-reference">Reference</th>
								<th class="col-client">Counterparty</th>
								<th class="col-site-address">Site address</th>
								<th class="col-date">Document date</th>
								<th v-if="showInvoiceColumn" class="col-invoice">Invoice</th>
								<th class="col-total">Amount</th>
							</tr>
						</thead>
						<tbody>
							<tr v-if="!filteredDocuments.length">
								<td :colspan="showInvoiceColumn ? 7 : 6" class="empty-cell">{{ emptyStateMessage }}</td>
							</tr>
							<tr
								v-for="item in filteredDocuments"
								:key="item.documentId || item.id"
								class="table-row"
								:class="{ active: String(activeSubmenuItemId) === String(resolveItemKey(item)) }"
								@click="toggleSubmenu(item, $event)"
								@contextmenu.prevent="openSubmenu(item, $event)"
							>
								<td class="col-type" v-tooltip="() => formatType(item.documentType)">
									{{ formatType(item.documentType) }}
								</td>
								<td class="col-reference" v-tooltip="() => item.reference || '-'">
									{{ item.reference || '-' }}
								</td>
								<td class="col-client" v-tooltip="() => formatCounterparty(item)">
									{{ formatCounterparty(item) }}
								</td>
								<td class="col-site-address" v-tooltip="() => formatSiteAddressTitle(item)">
									{{ formatSiteAddress(item) }}
								</td>
								<td class="col-date" v-tooltip="() => formatDate(item.documentDate)">
									{{ formatDate(item.documentDate) }}
								</td>
								<td v-if="showInvoiceColumn" class="col-invoice">
									<button
										v-if="resolveConvertedInvoice(item)"
										type="button"
										class="invoice-number-link"
										v-tooltip="() => formatInvoiceNumber(resolveConvertedInvoice(item))"
										:aria-label="`Open invoice ${formatInvoiceNumber(resolveConvertedInvoice(item))}`"
										@click.stop="handleOpenConvertedInvoice(item)"
									>
										{{ formatInvoiceNumber(resolveConvertedInvoice(item)) }}
									</button>
									<span v-else class="invoice-number-empty">—</span>
								</td>
								<td class="col-total numeric" v-tooltip="() => formatCurrency(item.totalAmount)">
									{{ formatCurrency(item.totalAmount) }}
								</td>
							</tr>
						</tbody>
					</table>

					<div
						v-if="activeSubmenuItemId !== null"
						ref="submenuElement"
						class="submenu"
						:style="submenuStyle"
						v-click-outside="closeSubmenu"
					>
						<button class="submenu-btn" type="button" @click.stop="handleEditSubmenuItem">
							Edit document
						</button>
						<button class="submenu-btn" type="button" @click.stop="handlePreviewSubmenuItem">
							Preview document
						</button>
						<button class="submenu-btn" type="button" @click.stop="handleDownloadSubmenuItem">
							Download PDF
						</button>
						<button
							v-if="canManageSupportingDocuments(activeSubmenuItem)"
							class="submenu-btn submenu-btn--supporting"
							type="button"
							@click.stop="handleSupportingDocumentsSubmenuItem"
						>
							<span>Supporting documents</span>
							<span
								v-if="supportingDocumentCount(activeSubmenuItem)"
								class="submenu-count"
							>
								{{ supportingDocumentCount(activeSubmenuItem) }}
							</span>
						</button>
						<button
							v-if="isResolvingQuotationInvoice(activeSubmenuItem)"
							class="submenu-btn"
							type="button"
							disabled
						>
							Checking invoice...
						</button>
						<button
							v-else-if="canConvertToInvoice(activeSubmenuItem)"
							class="submenu-btn convert"
							type="button"
							@click.stop="handleConvertToInvoiceSubmenuItem"
						>
							Convert to invoice
						</button>
						<button
							v-else-if="canOpenConvertedInvoice(activeSubmenuItem)"
							class="submenu-btn convert"
							type="button"
							@click.stop="handleOpenConvertedInvoiceSubmenuItem"
						>
							Open invoice
						</button>
						<button
							v-if="canTransferToSupplierDocuments(activeSubmenuItem)"
							class="submenu-btn transfer"
							type="button"
							@click.stop="handleTransferSubmenuItem"
						>
							Transfer to supplier documents
						</button>
						<button class="submenu-btn delete" type="button" @click.stop="handleDeleteSubmenuItem">
							Delete document
						</button>
					</div>
				</div>
				<div v-if="showRefreshingOverlay" class="table-loading-overlay">
					<div class="table-loading-indicator">
						<span class="table-loading-spinner" aria-hidden="true"></span>
						<span>Loading client documents...</span>
					</div>
				</div>
			</div>
		</div>

		<div v-if="showTransferModal" class="modal-overlay" @click="cancelTransfer">
			<div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="transfer-modal-title" @click.stop>
				<h3 id="transfer-modal-title" class="modal-title">Transfer to supplier documents</h3>
				<p class="modal-message">
					This will move {{ transferTarget?.reference || 'this assignment confirmation' }} to
					<strong>Supplier documents → Assignments</strong> and remove it from Client documents.
					No copy will remain here.
				</p>
				<div class="modal-actions">
					<button class="modal-btn cancel" type="button" :disabled="transferring" @click="cancelTransfer">
						Cancel
					</button>
					<button
						class="modal-btn transfer-confirm"
						type="button"
						:disabled="transferring"
						@click="confirmTransfer"
					>
						{{ transferring ? 'Transferring...' : 'Transfer' }}
					</button>
				</div>
			</div>
		</div>

		<div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
			<div class="modal-content" @click.stop>
				<h3 class="modal-title">Delete document</h3>
				<p class="modal-message">
					Are you sure you want to delete {{ deleteTarget?.reference || 'this document' }}?
				</p>
				<div class="modal-actions">
					<button class="modal-btn cancel" type="button" @click="cancelDelete">Cancel</button>
					<button
						class="modal-btn confirm"
						type="button"
						:disabled="deleting"
						@click="confirmDelete"
					>
						{{ deleting ? 'Deleting...' : 'Delete' }}
					</button>
				</div>
			</div>
		</div>

		<SupportingDocumentsModal
			:show="showSupportingDocumentsModal"
			:document="supportingDocumentsTarget"
			:loading="supportingDocumentsLoading"
			:saving="supportingDocumentsSaving"
			@close="closeSupportingDocumentsModal"
			@save="saveSupportingDocuments"
		/>

		<ProposalImportPreview
			v-if="proposalImport.file"
			:file="proposalImport.file"
			:logo-url="proposalImport.logoUrl"
			:footer-text="proposalImport.footerText"
			@saved="onProposalImported"
			@close="closeProposalImport"
		/>

		<InvoicePreview
			:show-preview="showPreview"
			:invoice-html="previewHtml"
			render-html-as-pdf
			:pdf-filename="
				previewDocument
					? deriveClientDocumentFilename(previewDocument)
					: 'document.pdf'
			"
			@close-preview="closePreview"
			@download-pdf="downloadPreviewPdf"
		/>
	</div>
</template>

<script setup>
import {
	computed,
	getCurrentInstance,
	nextTick,
	onMounted,
	onUnmounted,
	reactive,
	ref,
	watch,
} from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import SupportingDocumentsModal from '@/components/client-documents/SupportingDocumentsModal.vue';
import ProposalImportPreview from '@/components/client-documents/ProposalImportPreview.vue';
import vTooltip from '@/directives/tooltip';
import { useClientDocumentsStore } from '@/stores/clientDocuments';
import { sameRecords, useLiveRefresh } from '@/composables/useLiveRefresh';
import { useClientInvoicesStore } from '@/stores/clientInvoices';
import { useMediaStore } from '@/stores/media';
import { useProjectsStore } from '@/stores/projects';
import { convertImageToDataUrl } from '@/utils/imageUtils';
import {
	downloadProposalPdfFromHtml,
	formatGbpAmount,
	formatProposalDate,
} from '@/utils/proposalDocument';
import {
	createClientDocumentContent,
	deriveClientDocumentFilename,
	generateClientDocumentHtml,
	getClientDocumentDefinition,
	getClientDocumentLabel,
	getClientDocumentSiteAddress,
	resolveClientDocumentType,
} from '@/utils/clientDocumentRegistry';
import { ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE } from '@/utils/assignmentConfirmationDocument';
import { PROPOSAL_DOCUMENT_TYPE } from '@/utils/proposalDocument';
import { applyProposalTaskTemplatesToNewContent } from '@/utils/proposalPresets';
import { QUOTATION_DOCUMENT_TYPE } from '@/utils/quotationDocument';

const emit = defineEmits([
	'open-document',
	'convert-to-invoice',
	'open-invoice',
	'document-transferred',
]);

const clientDocumentsStore = useClientDocumentsStore();
const clientInvoicesStore = useClientInvoicesStore();
const projectsStore = useProjectsStore();
const mediaStore = useMediaStore();
const { proxy } = getCurrentInstance() || {};
const toast = proxy?.$toast;
const logError = proxy?.$logError;

const showCreateMenu = ref(false);
const creating = ref(false);
const activeSubmenuItemId = ref(null);
const submenuElement = ref(null);
const submenuPosition = reactive({ top: 0, left: 0 });
const showTransferModal = ref(false);
const transferTarget = ref(null);
const transferring = ref(false);
const showDeleteModal = ref(false);
const deleteTarget = ref(null);
const deleting = ref(false);
const showPreview = ref(false);
const previewHtml = ref('');
const previewDocument = ref(null);
const showSupportingDocumentsModal = ref(false);
const supportingDocumentsTarget = ref(null);
const supportingDocumentsLoading = ref(false);
const supportingDocumentsSaving = ref(false);
const search = ref('');
const activeCategory = ref('all');

const documents = computed(() => clientDocumentsStore.documentList || []);
const showInvoiceColumn = computed(
	() => activeCategory.value === QUOTATION_DOCUMENT_TYPE,
);
const hasAssignmentConfirmations = computed(() =>
	documents.value.some(
		(item) => resolveClientDocumentType(item) === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
	),
);
const categoryFilters = computed(() => [
	{ value: 'all', label: 'All categories' },
	{ value: QUOTATION_DOCUMENT_TYPE, label: 'Quotations' },
	{ value: PROPOSAL_DOCUMENT_TYPE, label: 'Proposals' },
	...(hasAssignmentConfirmations.value
		? [
				{
					value: ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
					label: 'Assignment confirmations',
				},
			]
		: []),
]);
const resolveItemKey = (item = {}) => item.documentId || item.id;
const resolveConvertedInvoice = (item = {}) => {
	const quotationId = resolveItemKey(item);
	if (quotationId === null || quotationId === undefined) return null;
	return clientInvoicesStore.quotationInvoiceLinks?.[String(quotationId)] || null;
};
const categoryDocuments = computed(() =>
	documents.value.filter(
		(item) =>
			activeCategory.value === 'all' ||
			resolveClientDocumentType(item) === activeCategory.value,
	),
);
const filteredDocuments = computed(() => {
	const q = String(search.value || '').trim().toLowerCase();
	if (!q) return categoryDocuments.value;
	return categoryDocuments.value.filter((item) => {
		const haystack = [
			item.title,
			item.reference,
			item.clientName,
			item.client?.name,
			item.propertySummary,
			getClientDocumentSiteAddress(item),
			formatType(item.documentType),
			formatDate(item.documentDate),
			formatCurrency(item.totalAmount),
			formatInvoiceNumber(resolveConvertedInvoice(item)),
		]
			.map((value) => String(value || '').toLowerCase())
			.join(' ');
		return haystack.includes(q);
	});
});
const activeCategoryLabel = computed(
	() =>
		categoryFilters.value.find((category) => category.value === activeCategory.value)?.label || '',
);
const emptyStateMessage = computed(() => {
	if (search.value.trim()) return 'No client documents match your search.';
	if (activeCategory.value === 'all') return 'No client documents yet.';
	return `No documents in ${activeCategoryLabel.value} yet.`;
});
const error = computed(() => clientDocumentsStore.error);
const showInitialLoading = computed(
	() => clientDocumentsStore.isLoading && !clientDocumentsStore.hasFetched,
);
const showRefreshingOverlay = computed(
	() => clientDocumentsStore.isRefreshing && clientDocumentsStore.hasFetched,
);
const activeSubmenuItem = computed(
	() => documents.value.find((item) => String(resolveItemKey(item)) === String(activeSubmenuItemId.value)) || null,
);
const submenuStyle = computed(() => ({
	top: `${submenuPosition.top}px`,
	left: `${submenuPosition.left}px`,
}));
const canTransferToSupplierDocuments = (item) =>
	item?.documentType === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE;
const isQuotation = (item) =>
	resolveClientDocumentType(item) === QUOTATION_DOCUMENT_TYPE;
const canManageSupportingDocuments = (item) =>
	[QUOTATION_DOCUMENT_TYPE, PROPOSAL_DOCUMENT_TYPE].includes(
		resolveClientDocumentType(item),
	);
const supportingDocumentCount = (item) =>
	(Array.isArray(item?.supportingDocuments) ? item.supportingDocuments : []).filter(
		(attachment) => attachment?.status !== 'delete',
	).length;
const isResolvingQuotationInvoice = (item) =>
	isQuotation(item) &&
	clientInvoicesStore.quotationInvoiceLinksLoading &&
	!clientInvoicesStore.quotationInvoiceLinksLoaded;
const canConvertToInvoice = (item) =>
	isQuotation(item) &&
	clientInvoicesStore.quotationInvoiceLinksLoaded &&
	!resolveConvertedInvoice(item);
const canOpenConvertedInvoice = (item) =>
	isQuotation(item) && Boolean(resolveConvertedInvoice(item));

const vClickOutside = {
	mounted(el, binding) {
		el.clickOutsideEvent = (event) => {
			if (!(el === event.target || el.contains(event.target))) {
				binding.value(event);
			}
		};
		document.body.addEventListener('click', el.clickOutsideEvent);
	},
	unmounted(el) {
		document.body.removeEventListener('click', el.clickOutsideEvent);
	},
};

const getLogoUrl = async () => {
	let resolvedLogoUrl = null;
	if (projectsStore.activeProject?.logo?.id) {
		try {
			const signed = await mediaStore.getSignedUrl(projectsStore.activeProject.logo.id);
			if (signed?.url) {
				resolvedLogoUrl = signed.url;
			}
		} catch (error) {
			logError?.(error, 'ClientDocumentsListView getLogoUrl');
		}
	}
	if (!resolvedLogoUrl && projectsStore.activeProject?.logo?.url) {
		resolvedLogoUrl = projectsStore.activeProject.logo.url;
	}
	resolvedLogoUrl = resolvedLogoUrl || '/logo-m.svg';
	const dataUrl = await convertImageToDataUrl(resolvedLogoUrl);
	return dataUrl || resolvedLogoUrl;
};

const refresh = async () => {
	try {
		await clientDocumentsStore.fetchDocuments({ force: true });
	} catch (error) {
		logError?.(error, 'ClientDocumentsListView refresh');
	}
};

const toggleCreateMenu = () => {
	showCreateMenu.value = !showCreateMenu.value;
	closeSubmenu();
};

const closeCreateMenu = () => {
	showCreateMenu.value = false;
};

watch(hasAssignmentConfirmations, (hasAssignments) => {
	if (!hasAssignments && activeCategory.value === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE) {
		activeCategory.value = 'all';
	}
});

const selectCategory = (category) => {
	activeCategory.value = category;
	closeSubmenu();
};

const getReusableFooterText = () =>
	documents.value
		.map((document) => document?.content?.footerText)
		.find((footerText) => String(footerText || '').trim());

const getPreviousQuotationContent = () =>
	documents.value.find(
		(document) => resolveClientDocumentType(document) === QUOTATION_DOCUMENT_TYPE,
	)?.content || null;

const applyPreviousQuotationText = (content, documentType) => {
	if (documentType !== QUOTATION_DOCUMENT_TYPE) return;
	const previousContent = getPreviousQuotationContent();
	if (!previousContent) return;

	const previousAdditionalCostNote = previousContent?.costBreakdown?.additionalTerms;
	if (typeof previousAdditionalCostNote === 'string') {
		content.costBreakdown.additionalTerms = previousAdditionalCostNote;
	}

	const previousAcceptanceText = previousContent?.acceptance?.text;
	if (typeof previousAcceptanceText === 'string') {
		content.acceptance.text = previousAcceptanceText;
	}
};

const createDocumentDraftId = (documentType) =>
	`${documentType}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createDocumentFromType = async (documentType) => {
	if (creating.value) return;
	creating.value = true;
	closeCreateMenu();
	const definition = getClientDocumentDefinition(documentType);
	try {
		const templateContent = createClientDocumentContent(documentType);
		const content = documentType === PROPOSAL_DOCUMENT_TYPE
			? applyProposalTaskTemplatesToNewContent(
				templateContent,
				projectsStore.activeProject?.documentPresets,
			)
			: templateContent;
		applyPreviousQuotationText(content, documentType);
		const reusableFooterText = getReusableFooterText();
		if (reusableFooterText) {
			content.footerText = reusableFooterText;
		}
		emit('open-document', {
			draftId: createDocumentDraftId(documentType),
			documentType: definition.type,
			initialContent: content,
			title: definition.label,
		});
	} catch (error) {
		logError?.(error, `ClientDocumentsListView create ${documentType}`);
		toast?.error?.(`Failed to create ${definition.lowerLabel}.`);
	} finally {
		creating.value = false;
	}
};

const handleCreateQuotation = async () => {
	await createDocumentFromType(QUOTATION_DOCUMENT_TYPE);
};

const handleCreateProposal = async () => {
	await createDocumentFromType(PROPOSAL_DOCUMENT_TYPE);
};

// A proposal written in Word is read on the server and shown before it is kept in Documents.
const proposalFileInput = ref(null);
const proposalImport = reactive({ file: null, logoUrl: '', footerText: '' });

const chooseProposalFile = () => {
	closeCreateMenu();
	proposalFileInput.value?.click();
};

const onProposalFileChosen = async (event) => {
	const file = event?.target?.files?.[0] || null;
	// The same file can be chosen again after a failed read.
	if (event?.target) event.target.value = '';
	if (!file) return;
	Object.assign(proposalImport, { file, logoUrl: '', footerText: getReusableFooterText() || '' });
	const logoUrl = await getLogoUrl();
	if (proposalImport.file === file) proposalImport.logoUrl = logoUrl;
};

const closeProposalImport = () => {
	Object.assign(proposalImport, { file: null, logoUrl: '', footerText: '' });
};

const onProposalImported = ({ document, open } = {}) => {
	closeProposalImport();
	toast?.success?.('Proposal saved to documents.');
	if (open && document) {
		emit('open-document', { documentId: document.documentId || document.id, document });
	}
};

const openSubmenu = (item, event) => {
	if (!item) return;
	if (event) {
		event.preventDefault();
		event.stopPropagation();
		if (event.target?.closest?.('.submenu-btn')) {
			return;
		}
	}
	closeCreateMenu();
	activeSubmenuItemId.value = resolveItemKey(item);
	nextTick(() => {
		calculateSubmenuPosition(event);
	});
};

const toggleSubmenu = (item, event) => {
	if (event) {
		event.stopPropagation();
		if (event.target?.closest?.('.submenu-btn')) {
			return;
		}
	}
	if (String(activeSubmenuItemId.value) === String(resolveItemKey(item))) {
		closeSubmenu();
		return;
	}
	openSubmenu(item, event);
};

const closeSubmenu = () => {
	activeSubmenuItemId.value = null;
};

const calculateSubmenuPosition = (event) => {
	const submenuRect = submenuElement.value?.getBoundingClientRect?.();
	const submenuButtonCount = submenuElement.value?.querySelectorAll?.('.submenu-btn').length || 4;
	const submenuWidth = submenuRect?.width || submenuElement.value?.offsetWidth || 180;
	const submenuHeight =
		submenuRect?.height || submenuElement.value?.offsetHeight || submenuButtonCount * 40 + 12;
	const padding = 12;

	let left = event?.clientX || 0;
	let top = event?.clientY || 0;

	if (left + submenuWidth > window.innerWidth - padding) {
		left = window.innerWidth - submenuWidth - padding;
	}
	left = Math.max(padding, left);

	if (top + submenuHeight > window.innerHeight - padding) {
		top = Math.max(padding, window.innerHeight - submenuHeight - padding);
	}
	top = Math.max(padding, top);

	submenuPosition.top = top;
	submenuPosition.left = left;
};

const handleEditSubmenuItem = () => {
	const item = activeSubmenuItem.value;
	closeSubmenu();
	if (!item) return;
	emit('open-document', {
		documentId: item.documentId || item.id,
		document: item,
	});
};

const buildPreviewHtml = async (document) => {
	const logoUrl = await getLogoUrl();
	return generateClientDocumentHtml({
		document,
		logoUrl,
	});
};

const handlePreviewSubmenuItem = async () => {
	const item = activeSubmenuItem.value;
	closeSubmenu();
	if (!item) return;
	try {
		previewHtml.value = await buildPreviewHtml(item);
		previewDocument.value = item;
		showPreview.value = true;
	} catch (error) {
		logError?.(error, 'ClientDocumentsListView handlePreviewSubmenuItem');
		toast?.error?.('Failed to preview document.');
	}
};

const handleDownloadSubmenuItem = async () => {
	const item = activeSubmenuItem.value;
	closeSubmenu();
	if (!item) return;
	try {
		const html = await buildPreviewHtml(item);
		await downloadProposalPdfFromHtml({
			html,
			filename: deriveClientDocumentFilename(item),
			logError,
		});
	} catch (error) {
		logError?.(error, 'ClientDocumentsListView handleDownloadSubmenuItem');
		toast?.error?.('Failed to download PDF.');
	}
};

const handleSupportingDocumentsSubmenuItem = async () => {
	const item = activeSubmenuItem.value;
	closeSubmenu();
	if (!canManageSupportingDocuments(item)) return;
	const documentId = resolveItemKey(item);
	showSupportingDocumentsModal.value = true;
	supportingDocumentsTarget.value = item;
	supportingDocumentsLoading.value = true;
	try {
		const document = await clientDocumentsStore.fetchDocument(documentId, {
			forceRefresh: true,
		});
		if (document) supportingDocumentsTarget.value = document;
	} catch (error) {
		logError?.(error, 'ClientDocumentsListView loadSupportingDocuments');
		toast?.error?.('Failed to load supporting documents.');
		showSupportingDocumentsModal.value = false;
		supportingDocumentsTarget.value = null;
	} finally {
		supportingDocumentsLoading.value = false;
	}
};

const closeSupportingDocumentsModal = () => {
	if (supportingDocumentsSaving.value) return;
	showSupportingDocumentsModal.value = false;
	supportingDocumentsTarget.value = null;
};

const saveSupportingDocuments = async (supportingDocuments) => {
	if (!supportingDocumentsTarget.value || supportingDocumentsSaving.value) return;
	supportingDocumentsSaving.value = true;
	try {
		const saved = await clientDocumentsStore.saveSupportingDocuments({
			documentId: resolveItemKey(supportingDocumentsTarget.value),
			supportingDocuments,
		});
		supportingDocumentsTarget.value = saved;
		toast?.success?.('Supporting documents saved.');
		showSupportingDocumentsModal.value = false;
		supportingDocumentsTarget.value = null;
	} catch (error) {
		logError?.(error, 'ClientDocumentsListView saveSupportingDocuments');
		toast?.error?.('Failed to save supporting documents.');
	} finally {
		supportingDocumentsSaving.value = false;
	}
};

const closePreview = () => {
	showPreview.value = false;
	previewHtml.value = '';
	previewDocument.value = null;
};

const downloadPreviewPdf = async () => {
	if (!previewDocument.value || !previewHtml.value) return;
	try {
		await downloadProposalPdfFromHtml({
			html: previewHtml.value,
			filename: deriveClientDocumentFilename(previewDocument.value),
			logError,
		});
	} catch (error) {
		logError?.(error, 'ClientDocumentsListView downloadPreviewPdf');
		toast?.error?.('Failed to download PDF.');
	}
};

const handleDeleteSubmenuItem = () => {
	deleteTarget.value = activeSubmenuItem.value;
	closeSubmenu();
	showDeleteModal.value = Boolean(deleteTarget.value);
};

const handleConvertToInvoiceSubmenuItem = () => {
	const item = activeSubmenuItem.value;
	closeSubmenu();
	if (!canConvertToInvoice(item)) return;
	emit('convert-to-invoice', {
		quotationId: item.documentId || item.id,
		quotation: item,
	});
};

const handleOpenConvertedInvoice = (item) => {
	const invoice = resolveConvertedInvoice(item);
	if (!invoice) return;
	emit('open-invoice', {
		listId: invoice.listId,
		documentId: invoice.documentId || invoice.id,
		invoice,
	});
};

const handleOpenConvertedInvoiceSubmenuItem = () => {
	const item = activeSubmenuItem.value;
	closeSubmenu();
	handleOpenConvertedInvoice(item);
};

const handleTransferSubmenuItem = () => {
	const item = activeSubmenuItem.value;
	closeSubmenu();
	if (!canTransferToSupplierDocuments(item)) return;
	transferTarget.value = item;
	showTransferModal.value = true;
};

const cancelTransfer = () => {
	if (transferring.value) return;
	showTransferModal.value = false;
	transferTarget.value = null;
};

const confirmTransfer = async () => {
	if (!transferTarget.value || transferring.value) return;
	const sourceDocumentId = transferTarget.value.documentId || transferTarget.value.id;
	transferring.value = true;
	try {
		const result = await clientDocumentsStore.transferToSupplierDocuments(sourceDocumentId);
		emit('document-transferred', {
			sourceDocumentId,
			destinationDocumentId: result?.document?.id ?? null,
			destinationCategory: 'assignments',
		});
		toast?.success?.('Moved to Supplier documents → Assignments.');
		showTransferModal.value = false;
		transferTarget.value = null;
	} catch (error) {
		logError?.(error, 'ClientDocumentsListView confirmTransfer');
		toast?.error?.('Failed to transfer document. The client document was not removed.');
	} finally {
		transferring.value = false;
	}
};

const cancelDelete = () => {
	if (deleting.value) return;
	showDeleteModal.value = false;
	deleteTarget.value = null;
};

const confirmDelete = async () => {
	if (!deleteTarget.value || deleting.value) return;
	deleting.value = true;
	try {
		await clientDocumentsStore.deleteDocument(deleteTarget.value.documentId || deleteTarget.value.id);
		toast?.success?.('Document deleted.');
		showDeleteModal.value = false;
		deleteTarget.value = null;
	} catch (error) {
		logError?.(error, 'ClientDocumentsListView confirmDelete');
		toast?.error?.('Failed to delete document.');
	} finally {
		deleting.value = false;
	}
};

const handleDocumentClick = (event) => {
	const target = event.target;
	if (!target?.closest?.('.create-wrapper')) {
		closeCreateMenu();
	}
};

const formatType = (value) => getClientDocumentLabel(value);
const formatCounterparty = (item) => item?.clientName || item?.client?.name || '-';
const formatSiteAddressTitle = (item) => getClientDocumentSiteAddress(item);
const formatSiteAddress = (item) => formatSiteAddressTitle(item) || '-';
const formatCurrency = (value) => (value === null || value === undefined || value === '' ? '-' : formatGbpAmount(value));
const formatDate = (value) => formatProposalDate(value) || '-';
const formatInvoiceNumber = (invoice) => {
	const number = invoice?.invoiceNumber || invoice?.documentId || invoice?.id;
	return number ? `#${number}` : '';
};
onMounted(() => {
	void clientDocumentsStore.fetchDocuments().catch((error) => {
		logError?.(error, 'ClientDocumentsListView mounted fetch');
	});
	void Promise.resolve(clientInvoicesStore.fetchQuotationInvoiceLinks()).catch((error) => {
		logError?.(error, 'ClientDocumentsListView mounted invoice link fetch');
	});
	document.addEventListener('click', handleDocumentClick);
});

onUnmounted(() => {
	document.removeEventListener('click', handleDocumentClick);
});

// Documents added or changed on another device show up here without reloading. The list is read
// again quietly, and only replaced while nothing on it is open.
const isDocumentListBusy = () =>
	!clientDocumentsStore.hasFetched ||
	clientDocumentsStore.isLoading ||
	clientDocumentsStore.isRefreshing ||
	showCreateMenu.value ||
	Boolean(activeSubmenuItemId.value) ||
	showTransferModal.value ||
	transferring.value ||
	showDeleteModal.value ||
	deleting.value ||
	showPreview.value ||
	showSupportingDocumentsModal.value ||
	Boolean(proposalImport.file);

const refreshDocumentsQuietly = async ({ canApply }) => {
	if (isDocumentListBusy()) return;
	const { items, pagination } = await clientDocumentsStore.peekDocuments();
	if (!canApply() || isDocumentListBusy()) return;
	if (sameRecords(clientDocumentsStore.items, items)) return;
	clientDocumentsStore.applyDocuments(items, pagination);
};

useLiveRefresh(refreshDocumentsQuietly, { isBusy: isDocumentListBusy });
</script>

<style scoped>
/* The Word file picker opens from the New document menu. */
.proposal-file-input {
	display: none;
}

.client-documents-list {
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 16px;
	box-sizing: border-box;
	padding: 24px;
	color: var(--text-color);
}

.list-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	flex-wrap: wrap;
	min-height: 40px;
}

.list-title-group {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.list-title {
	margin: 0;
	font: 600 1.5rem/2rem var(--font-family-display);
	color: var(--text-color);
}

.link-btn {
	height: 34px;
	padding: 0 12px;
	border: 0;
	background: transparent;
	color: var(--noc-blue);
	font: inherit;
	cursor: pointer;
}

.content-wrapper {
	position: relative;
	display: flex;
	flex-direction: column;
}

.table-wrapper {
	position: relative;
	min-width: 0;
	display: flex;
	flex-direction: column;
}

.documents-table-container {
	width: 100%;
	min-width: 0;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}

.documents-table {
	width: 100%;
	min-width: 960px;
	max-width: 100%;
	box-sizing: border-box;
	table-layout: fixed;
	border-collapse: separate;
	border-spacing: 0;
	border-inline: 1px solid var(--border-color);
	font: 400 0.875rem/1.25rem var(--font-family-base);
}

.documents-table th,
.documents-table td {
	box-sizing: border-box;
	text-align: left;
	padding: 8px 16px;
	border-bottom: 1px solid var(--border-color);
	vertical-align: middle;
}

.documents-table td {
	height: 40px;
}

.documents-table tbody {
	background: var(--noc-background);
}

.documents-table tbody tr:not(:last-child) td:first-child {
	border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.documents-table th {
	height: 38px;
	color: var(--text-color);
	font: 600 0.8125rem/1.125rem var(--font-family-base);
	background: #fafafa;
	border-top: 1px solid var(--border-color);
	position: sticky;
	top: 0;
	z-index: 1;
}

.table-row {
	touch-action: manipulation;
	cursor: pointer;
	transition: background-color 0.15s;
}

.table-row.active {
	background: var(--noc-blue-05);
}

.col-type {
	width: 12%;
}

.col-reference {
	width: 17%;
	font-weight: 600;
}

.col-client {
	width: 18%;
}

.col-site-address {
	width: 23%;
}

.col-date {
	width: 16%;
}

.col-total {
	width: 14%;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
	overflow: visible;
	text-overflow: clip;
}

.col-invoice {
	width: 12%;
}

.documents-table--with-invoice .col-type {
	width: 10%;
}

.documents-table--with-invoice .col-reference {
	width: 14%;
}

.documents-table--with-invoice .col-client {
	width: 16%;
}

.documents-table--with-invoice .col-site-address {
	width: 20%;
}

.documents-table--with-invoice .col-date {
	width: 14%;
}

.documents-table--with-invoice .col-total {
	width: 14%;
}

.invoice-number-link {
	max-width: 100%;
	padding: 0;
	border: 0;
	background: transparent;
	color: var(--noc-blue, #0065ff);
	font: inherit;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.invoice-number-link:hover {
	text-decoration: underline;
}

.invoice-number-link:focus-visible {
	outline: 2px solid rgba(0, 101, 255, 0.28);
	outline-offset: 3px;
	border-radius: 2px;
}

.invoice-number-empty {
	color: var(--text-color-secondary, #6b7280);
}

.col-reference,
.col-type,
.col-client,
.col-site-address,
.col-date {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.documents-table .numeric {
	text-align: right;
}

.documents-table th.col-total {
	text-align: right;
}

.documents-table th.col-total,
.documents-table td.col-total {
	padding-right: 16px;
}

.empty-cell,
.loading-state,
.error-state {
	padding: 28px;
	text-align: center;
	color: var(--text-color-secondary);
}

.error-state {
	color: var(--noc-red);
}

.table-loading-overlay {
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.82);
	z-index: 20;
	pointer-events: auto;
}

.table-loading-indicator {
	position: absolute;
	top: 10px;
	left: 50%;
	transform: translateX(-50%);
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 10px;
	border: 1px solid var(--border-color, #dcdcdc);
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.96);
	color: var(--text-color-secondary, #4b5563);
	font-size: 0.78rem;
	font-weight: 600;
	letter-spacing: 0.01em;
}

.table-loading-spinner {
	width: 16px;
	height: 16px;
	border-radius: 50%;
	border: 2px solid #d3d7df;
	border-top-color: var(--noc-blue, #0065ff);
	animation: spin 0.8s linear infinite;
}

.submenu {
	border: 1px solid var(--border-color);
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	position: fixed;
	z-index: 1000;
	width: max-content;
	max-width: calc(100vw - 24px);
	padding: 6px 0;
	background-color: var(--noc-background);
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 10px 16px;
	background: none;
	border: none;
	color: var(--text-color);
	text-align: left;
	font: 500 0.875rem/1.25rem var(--font-family-base);
	cursor: pointer;
	touch-action: manipulation;
	white-space: nowrap;
}

.submenu-btn:hover {
	background-color: var(--noc-blue-10);
	color: var(--noc-blue);
}

.submenu-btn--supporting {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
}

.submenu-count {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 22px;
	height: 20px;
	padding: 0 6px;
	border-radius: 999px;
	background: var(--noc-blue-10, rgba(0, 101, 255, 0.1));
	color: var(--noc-blue, #0065ff);
	font-size: 0.72rem;
	font-weight: 700;
}

.submenu-btn:disabled {
	color: var(--text-color-secondary, #6b7280);
	cursor: wait;
	opacity: 0.7;
}

.submenu-btn.transfer {
	color: var(--noc-blue);
}

.submenu-btn.delete {
	color: #dc2626;
}

.submenu-btn.delete:hover {
	background-color: #fee2e2;
}

.modal-overlay {
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(15, 23, 42, 0.45);
}

.modal-content {
	width: min(420px, calc(100vw - 32px));
	background: var(--noc-background);
	border-radius: 6px;
	padding: 22px;
	box-shadow: 0 18px 50px rgba(15, 23, 42, 0.24);
}

.modal-title {
	margin: 0 0 8px;
}

.modal-message {
	margin: 0 0 18px;
	color: var(--text-color-secondary);
}

.modal-actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
}

.modal-btn {
	height: 36px;
	padding: 0 14px;
	border-radius: 4px;
	border: 1px solid var(--border-color);
	background: var(--noc-background);
	color: var(--text-color);
	cursor: pointer;
}

.modal-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.modal-btn.transfer-confirm {
	background: var(--noc-blue);
	color: #fff;
	border-color: var(--noc-blue);
}

.modal-btn.confirm {
	background: var(--noc-red);
	color: #fff;
	border-color: var(--noc-red);
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
@media (hover: hover) {
	.table-row:hover { background: var(--noc-blue-05); }
}

</style>

<style scoped src="@/assets/styles/document-list-controls.css"></style>
